import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiHeart, 
  FiShare2, 
  FiMapPin, 
  FiCalendar, 
  FiUsers, 
  FiDroplet,
  FiChevronLeft,
  FiChevronRight,
  FiArrowRight,
  FiStar,
  FiZap
} from 'react-icons/fi'; 
import { 
  FaGasPump, 
  FaCar, 
  FaCog, 
  FaPalette,
  FaShieldAlt,
  FaTachometerAlt,
  FaHome,
  FaCode,
  FaSubway,
  FaRunning,
  FaInfoCircle
} from 'react-icons/fa';
import './ProfilCar.css';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';
import { useParams } from 'react-router-dom';

const ProfilCar = () => {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [showAllEquipements, setShowAllEquipements] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // AJOUT: Définition des animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        duration: 0.8
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const imageVariants = {
    hidden: { 
      opacity: 0, 
      scale: 1.1,
      rotateX: 5 
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      rotateX: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      rotateX: -5,
      transition: {
        duration: 0.4
      }
    }
  };

  const fetchCar = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_CONFIG.apiUrl}get_vehicle.php?id=${id}`, { 
        timeout: 10000 
      });

      if (response.data.success) {
        setCar(response.data.vehicle);
      } else {
        setError(response.data.message || 'Véhicule introuvable');
      }
    } catch (err) {
      console.error('Erreur API:', err);
      setError(err.response?.data?.message || 'Impossible de charger les données du véhicule');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchCar();
  }, [id]);

  if (loading) {
    return (
      <div className="loading-container">
        <motion.div 
          className="loading-spinner"
          animate={{ 
            rotate: 360,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            rotate: { duration: 1, repeat: Infinity, ease: "linear" },
            scale: { duration: 1.5, repeat: Infinity }
          }}
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, repeat: Infinity, repeatType: "reverse", duration: 1 }}
        >
          Chargement du véhicule...
        </motion.p>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div 
        className="error-container"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
      >
        <div className="error-icon">⚡</div>
        <h3>Erreur de chargement</h3>
        <p>{error}</p>
        <motion.button 
          className="retry-button"
          onClick={fetchCar}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Réessayer
        </motion.button>
      </motion.div>
    );
  }

  if (!car) return null;

  // Calculs pour les images, équipements et options
  const nextImage = () => setSelectedImage((prev) => (prev + 1) % car.images.length);
  const prevImage = () => setSelectedImage((prev) => (prev - 1 + car.images.length) % car.images.length);
  
  // Calculs pour les équipements et options
  const equipements = car.equipements || [];
  const options = car.options || [];
  const visibleEquipements = showAllEquipements ? equipements : equipements.slice(0, 6);
  const visibleOptions = showAllOptions ? options : options.slice(0, 6);

  return (
    <motion.div 
      className="profil-car-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* En-tête élégant */}
      <motion.div 
        className="car-header"
        variants={cardVariants}
        whileHover={{ y: -2 }}
      >
        <div className="header-content">
          <div className="title-section">
            <motion.div 
              className="car-badge"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
            >
              <FiZap className="badge-icon" />
              {car.statut}
            </motion.div>
            <h1 className="car-title">
              <span className="brand">{car.marque}</span>
              <span className="model">{car.modele}</span>
            </h1>
            <div className="car-subtitle">
              <span>Année : {car.annee} | </span>
              <span>Kilometrage: {car.kilometrage?.toLocaleString()} km</span>
            </div>
          </div>
          
          <div className="price-section">
            <div className="price-main">
              <motion.span 
                className="price"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: "spring" }}
              >
                {car.prix?.toLocaleString()} $
              </motion.span>
              <span className="price-label">Prix TTC</span>
            </div>
            <div className="action-buttons">
              <motion.button 
                className={`like-button ${isLiked ? 'liked' : ''}`}
                onClick={() => setIsLiked(!isLiked)}
                whileHover={{ 
                  scale: 1.1,
                  rotate: isLiked ? 0 : 10 
                }}
                whileTap={{ scale: 0.9 }}
                animate={{
                  scale: isLiked ? [1, 1.2, 1] : 1
                }}
                transition={{ duration: 0.3 }}
              >
                <FiHeart />
              </motion.button>
              <motion.button 
                className="share-button"
                whileHover={{ scale: 1.1, rotate: -5 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiShare2 />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Galerie immersive */}
      <motion.section 
        className="gallery-section"
        variants={cardVariants}
      >
        <div className="main-image-container">
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedImage}
              className="image-wrapper"
              variants={imageVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {!imageLoaded && (
                <div className="image-skeleton">
                  <div className="skeleton-shimmer" />
                </div>
              )}
              <img
                src={`${API_CONFIG.apiUrl}carpic/${car.images[selectedImage]}`}
                alt={`${car.marque} ${car.modele}`}
                className="main-image"
                onLoad={() => setImageLoaded(true)}
                style={{ opacity: imageLoaded ? 1 : 0 }}
              />
            </motion.div>
          </AnimatePresence>

          <motion.button 
            className="nav-button prev-button"
            onClick={prevImage}
            whileHover={{ 
              scale: 1.15, 
              x: -2,
              backgroundColor: "rgba(0,0,0,0.9)" 
            }}
            whileTap={{ scale: 0.9 }}
          >
            <FiChevronLeft />
          </motion.button>
          
          <motion.button 
            className="nav-button next-button"
            onClick={nextImage}
            whileHover={{ 
              scale: 1.15, 
              x: 2,
              backgroundColor: "rgba(0,0,0,0.9)" 
            }}
            whileTap={{ scale: 0.9 }}
          >
            <FiChevronRight />
          </motion.button>

          <motion.div 
            className="image-indicator"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <span className="image-counter">{selectedImage + 1} / {car.images.length}</span>
          </motion.div>

          <div className="image-overlay">
            <motion.button 
              className="zoom-button"
              whileHover={{ scale: 1.05, y: -1 }}
              whileTap={{ scale: 0.95 }}
            >
              🔍 Voir en détail
            </motion.button>
          </div>
        </div>

        <div className="thumbnails-container">
          {car.images.map((img, i) => (
            <motion.div
              key={i}
              className={`thumbnail ${i === selectedImage ? 'active' : ''}`}
              onClick={() => setSelectedImage(i)}
              whileHover={{ 
                scale: 1.08,
                y: -3
              }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ 
                delay: 0.6 + i * 0.1,
                type: "spring",
                stiffness: 150
              }}
            >
              <img 
                src={`${API_CONFIG.apiUrl}carpic/${img}`} 
                alt={`Vue ${i + 1}`} 
              />
              <div className="thumbnail-overlay" />
              {i === selectedImage && (
                <motion.div 
                  className="active-indicator"
                  layoutId="activeThumbnail"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Grille d'information moderne */}
      <div className="info-grid">
        {/* Caractéristiques principales */}
        <motion.section 
          className="specs-card glass-card"
          variants={cardVariants}
          whileHover={{ y: -3, scale: 1.01 }}
        >
          <div className="card-header">
            <div className="icon-wrapper">
              <FaCar className="card-icon" />
            </div>
            <h3 className="section-title">Caractéristiques</h3>
          </div>
          <div className="specs-grid">
            {[
              { icon: FiCalendar, label: "Année", value: car.annee },
              { icon: FaRunning, label: "Kilométrage", value: `${car.kilometrage?.toLocaleString()} km` },
              { icon: FaGasPump, label: "Énergie", value: car.carburant },
              { icon: FaCode, label: "Couleur", value: car.immatriculation },
            ].map((spec, index) => (
              <motion.div 
                key={spec.label}
                className="spec-item"
                whileHover={{ 
                  scale: 1.03, 
                  y: -2,
                  transition: { type: "spring", stiffness: 300 }
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
              >
                <div className="spec-icon-wrapper">
                  <spec.icon className="spec-icon" />
                </div>
                <div className="spec-content">
                  <span className="spec-label">{spec.label}</span>
                  <span className="spec-value">{spec.value}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Section Équipements */}
        <motion.section 
          className="features-card glass-card"
          variants={cardVariants}
          whileHover={{ y: -3, scale: 1.01 }}
        >
          <div className="card-header">
            <div className="icon-wrapper">
              <FaCog className="card-icon" />
            </div>
            <h3 className="section-title">Équipements</h3>
          </div>
          <div className="features-grid">
            {visibleEquipements.map((equipement, i) => (
              <motion.div 
                key={i}
                className="feature-item"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 + i * 0.1 }}
                whileHover={{ 
                  x: 8,
                  transition: { type: "spring", stiffness: 200 }
                }}
              >
                <motion.div 
                  className="feature-dot"
                  whileHover={{ scale: 1.5 }}
                />
                <span>{equipement}</span>
              </motion.div>
            ))}
            {equipements.length === 0 && (
              <div className="no-features">
                Aucun équipement spécifié
              </div>
            )}
          </div>
          
          {equipements.length > 6 && (
            <motion.button 
              className="show-more-button"
              onClick={() => setShowAllEquipements(!showAllEquipements)}
              whileHover={{ scale: 1.02, x: 5 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>
                {showAllEquipements 
                  ? 'Voir moins' 
                  : `Voir ${equipements.length - 6} équipements supplémentaires`
                }
              </span>
              <motion.div
                animate={{ rotate: showAllEquipements ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <FiArrowRight />
              </motion.div>
            </motion.button>
          )}
        </motion.section>

        {/* Section Options */}
        <motion.section 
          className="features-card glass-card"
          variants={cardVariants}
          whileHover={{ y: -3, scale: 1.01 }}
        >
          <div className="card-header">
            <div className="icon-wrapper">
              <FaPalette className="card-icon" />
            </div>
            <h3 className="section-title">Options</h3>
          </div>
          <div className="features-grid">
            {visibleOptions.map((option, i) => (
              <motion.div 
                key={i}
                className="feature-item"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2 + i * 0.1 }}
                whileHover={{ 
                  x: 8,
                  transition: { type: "spring", stiffness: 200 }
                }}
              >
                <motion.div 
                  className="feature-dot"
                  whileHover={{ scale: 1.5 }}
                />
                <span>{option}</span>
              </motion.div>
            ))}
            {options.length === 0 && (
              <div className="no-features">
                Aucune option spécifiée
              </div>
            )}
          </div>
          
          {options.length > 6 && (
            <motion.button 
              className="show-more-button"
              onClick={() => setShowAllOptions(!showAllOptions)}
              whileHover={{ scale: 1.02, x: 5 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>
                {showAllOptions 
                  ? 'Voir moins' 
                  : `Voir ${options.length - 6} options supplémentaires`
                }
              </span>
              <motion.div
                animate={{ rotate: showAllOptions ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <FiArrowRight />
              </motion.div>
            </motion.button>
          )}
        </motion.section>

        {/* Description */}
        <motion.section 
          className="description-card glass-card"
          variants={cardVariants}
          whileHover={{ y: -3, scale: 1.01 }}
        >
          <div className="card-header">
            <div className="icon-wrapper">
              <FaInfoCircle className="card-icon" />
            </div>
            <h3 className="section-title">Description</h3>
          </div>
          <p className="description-text">
            {car.description || `${car.marque} ${car.modele} en excellent état. Véhicule entretenu avec soin et prêt à rouler.`}
          </p>
          
  
        </motion.section>
      </div>

      {/* Actions flottantes élégantes */}
      <motion.div 
        className="floating-actions"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, type: "spring" }}
      >
        <motion.div 
          className="price-bubble"
          whileHover={{ scale: 1.05 }}
        >
          <span className="bubble-price">{car.prix?.toLocaleString()} $</span>
        </motion.div>
        <div className="action-buttons-group">
          <motion.button 
            className="primary-action"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiUsers className="button-icon" />
            Contacter
          </motion.button>
          <motion.button 
            className="secondary-action"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiCalendar className="button-icon" />
            Essai
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProfilCar;